import { cn } from '@/helpers/classnames';
import Input from '../core/Input';
import {
  useController,
  UseControllerProps,
  FieldValues,
  FieldError,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { ReactNode } from 'react';

interface InputPostalCodeFieldProps<
  T extends FieldValues,
> extends UseControllerProps<T> {
  label?: string;
  editMode?: boolean;
  placeholder?: string;
  noValueDescription?: ReactNode;
  error?: FieldError;
  helperText?: string;
  helperTextReserveSpace?: boolean;
  onBlur?: () => void;
  className?: string;
  required?: boolean;
}

export const InputPostalCode = <T extends FieldValues>({
  label,
  editMode,
  placeholder,
  noValueDescription,
  error,
  helperText,
  helperTextReserveSpace = false,
  onBlur,
  className,
  required,
  ...props
}: InputPostalCodeFieldProps<T>) => {
  const { field } = useController(props);
  const { formatMessage } = useIntl();

  const handleChange = (value: string) => {
    let cleaned = value.replace(/[^\d\s]/g, '');

    const digitsOnly = cleaned.replace(/\s/g, '');
    if (digitsOnly.length > 5) return;

    if (digitsOnly.length > 3) {
      cleaned = `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3)}`;
    } else {
      cleaned = digitsOnly;
    }

    field.onChange(cleaned);
  };

  const handleBlur = () => {
    field.onBlur();
    onBlur?.();
  };

  return editMode === true ? (
    <Input
      id={field.name}
      type="text"
      value={field.value || ''}
      placeholder={placeholder || '123 45'}
      className={className}
      label={label}
      onChange={(e) => handleChange(e.target.value)}
      error={error?.message}
      onBlur={handleBlur}
      helperText={helperText}
      helperTextReserveSpace={helperTextReserveSpace}
      maxLength={6}
      required={required}
    />
  ) : (
    <span className={cn(!field.value && 'italic text-text-700', className)}>
      {field.value ||
        noValueDescription ||
        formatMessage({ defaultMessage: 'Inget värde' })}
    </span>
  );
};
